import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Cpu, Check, ChevronDown, Loader2, KeyRound, Settings as SettingsIcon } from "lucide-react";
import { providersApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

/**
 * Model version picker for the run's default provider (chosen in Settings).
 * The pill shows "Provider · version" and the menu lists only that provider's
 * model versions — provider switching lives in Settings to keep this focused.
 *
 * provider: the default provider object (or null if none configured)
 * value:    the selected version string
 * onChange: (version) => void
 */
export function ModelPicker({ provider, value, onChange, variant = "dark", className }) {
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset the cached list whenever the default provider changes.
  useEffect(() => { setVersions(null); }, [provider?.id]);

  const load = useCallback(async () => {
    if (!provider) return;
    if (versions) return;
    if (provider.local) { setVersions([provider.defaultModel].filter(Boolean)); return; }
    setLoading(true);
    try {
      const res = await providersApi.models(provider.id);
      setVersions(res?.models?.length ? res.models : [provider.defaultModel].filter(Boolean));
    } catch (_) {
      setVersions([provider.defaultModel].filter(Boolean));
    } finally {
      setLoading(false);
    }
  }, [provider, versions]);

  const dark = variant !== "light";
  const pillCls = dark
    ? "border-white/15 bg-white/10 text-slate-100 hover:bg-white/[0.16]"
    : "border-[color:var(--border-subtle)] bg-card text-slate-800 hover:bg-[color:var(--surface-1)]";

  const activeVersion = value || provider?.defaultModel || "";
  const list = versions || (provider ? [provider.defaultModel].filter(Boolean) : []);

  return (
    <DropdownMenu onOpenChange={(open) => open && load()}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-colors", pillCls, className)}
          data-testid="model-picker-button"
        >
          <Cpu className="h-3.5 w-3.5 opacity-80" />
          {provider
            ? <span className="max-w-[220px] truncate">{provider.label}{activeVersion ? ` · ${activeVersion}` : ""}</span>
            : <span>Select a model</span>}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 max-h-[60vh] overflow-y-auto" data-testid="model-picker-menu">
        {!provider ? (
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex items-center gap-2" data-testid="model-picker-no-keys">
              <KeyRound className="h-3.5 w-3.5" /> Add a model key in Settings
            </Link>
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {provider.label}
              {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            </DropdownMenuLabel>
            {list.length === 0 ? (
              <DropdownMenuItem disabled className="text-[12px] text-muted-foreground">No versions found</DropdownMenuItem>
            ) : (
              list.map((v) => (
                <DropdownMenuItem
                  key={v}
                  onSelect={() => onChange?.(v)}
                  className="flex items-center justify-between gap-2"
                  data-testid={`model-option-${v}`}
                >
                  <span className="truncate text-[12.5px]">{v}</span>
                  {v === activeVersion && <Check className="h-3.5 w-3.5 shrink-0 text-[color:var(--accent-cyan)]" />}
                </DropdownMenuItem>
              ))
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center gap-2 text-[12px] text-muted-foreground" data-testid="model-picker-settings">
                <SettingsIcon className="h-3.5 w-3.5" /> Change provider in Settings
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
